import React from 'react'
import { Button, Input, Select, StatCards, Skeleton, Pagination } from '@/shared/ui'
import { CreditCard, TrendingUp, Timer, AlertTriangle, Search } from 'lucide-react'
import type { SubscriptionItem } from '../types'
import { listSubscriptionsV2 } from '../services/subscriptionServiceV2'
import { BulkSelectBar } from '@/shared/ui/BulkSelectBar'
import { listAccountInvoices, type InvoiceHistoryRow } from '../services/subscriptionHistoryService'
import SubscriptionCardFuturistic from '../components/SubscriptionCardFuturistic'
import { getSubscriptionStats, type SubscriptionStats } from '../services/subscriptionStatsService'

export default function SubscriptionsV2Page() {
  const [items, setItems] = React.useState<SubscriptionItem[]>([])
  const [stats, setStats] = React.useState<SubscriptionStats>({
    total: 0,
    approved: 0,
    pending: 0,
    past_due: 0,
    inactive: 0,
    trial: 0,
  })
  const [page, setPage] = React.useState(1)
  const [pageSize] = React.useState(50)
  const [total, setTotal] = React.useState(0)
  const [search, setSearch] = React.useState('')
  const [status, setStatus] = React.useState('')
  const [loading, setLoading] = React.useState(true)
  const [selected, setSelected] = React.useState<Set<string>>(new Set())
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set())
  const [histories, setHistories] = React.useState<Record<string, { rows: InvoiceHistoryRow[]; total: number }>>({})

  async function load() {
    setLoading(true)
    try {
      const res = await listSubscriptionsV2({ search, status, page, pageSize })
      setItems(res.items)
      setTotal(res.total)
      // Usa o mesmo agregador de stats da página v1 para manter números alinhados com o Stripe
      const agg = await getSubscriptionStats()
      setStats(agg)
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status])

  async function toggleExpand(accId: string) {
    setExpanded((prev) => {
      const n = new Set(prev)
      if (n.has(accId)) n.delete(accId)
      else n.add(accId)
      return n
    })
    if (!histories[accId]) {
      try {
        const h = await listAccountInvoices(accId, 1, 10)
        setHistories((m) => ({ ...m, [accId]: h }))
      } catch {
        // ignore
      }
    }
  }

  const cardsStats = React.useMemo(
    () => [
      {
        label: 'Assinaturas',
        value: stats.total.toLocaleString('pt-BR'),
        icon: <CreditCard className="h-5 w-5" />,
        tone: 'primary' as const,
        onClick: () => {
          setStatus('')
          setPage(1)
        },
        active: status === '',
      },
      {
        label: 'Aprovadas',
        value: stats.approved.toLocaleString('pt-BR'),
        icon: <TrendingUp className="h-5 w-5" />,
        tone: 'success' as const,
        onClick: () => {
          setStatus('active')
          setPage(1)
        },
        active: status === 'active',
      },
      {
        label: 'Pendentes (checkout)',
        value: stats.pending.toLocaleString('pt-BR'),
        icon: <Timer className="h-5 w-5" />,
        tone: 'warning' as const,
        onClick: () => {
          setStatus('incomplete')
          setPage(1)
        },
        active: status === 'incomplete',
      },
      {
        label: 'Vencidas',
        value: stats.past_due.toLocaleString('pt-BR'),
        icon: <AlertTriangle className="h-5 w-5" />,
        tone: 'warning' as const,
        onClick: () => {
          setStatus('past_due')
          setPage(1)
        },
        active: status === 'past_due',
      },
      {
        label: 'Inativos',
        value: stats.inactive.toLocaleString('pt-BR'),
        icon: <AlertTriangle className="h-5 w-5" />,
        tone: 'violet' as const,
        onClick: () => {
          setStatus('canceled')
          setPage(1)
        },
        active: status === 'canceled',
      },
      {
        label: 'Trial',
        value: stats.trial.toLocaleString('pt-BR'),
        icon: <Timer className="h-5 w-5" />,
        tone: 'warning' as const,
        onClick: () => {
          setStatus('trialing')
          setPage(1)
        },
        active: status === 'trialing',
      },
    ],
  [stats, status],
  )

  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize))

  return (
    <div className="mx-auto w-full max-w-[1600px] px-2 pb-10 pt-4">
      <div className="mb-6 flex flex-col gap-2">
        <h1 className="bg-gradient-to-r from-emerald-300 via-cyan-300 to-sky-400 bg-clip-text text-3xl font-semibold text-transparent">
          Assinaturas · Visão Futurista (beta)
        </h1>
        <p className="text-sm text-foreground/70">
          Painel experimental com status por conta, focado em clareza de cobrança e checkouts pendentes.
        </p>
      </div>

      <StatCards items={cardsStats} className="mb-6 lg:grid-cols-6 gap-3" />

      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/60" />
          <Input
            placeholder="Buscar por cliente, e-mail ou plano"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-slate-950/80 border-slate-800/80"
          />
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value)
              setPage(1)
            }}
          >
            <option value="">Todos</option>
            <option value="active">Ativas</option>
            <option value="trialing">Em teste</option>
            <option value="incomplete">Pendentes (checkout)</option>
            <option value="past_due">Vencidas</option>
            <option value="canceled">Canceladas</option>
          </Select>
          <Button onClick={() => load()} disabled={loading}>
            Atualizar
          </Button>
        </div>
      </div>

      <BulkSelectBar
        selectedCount={selected.size}
        totalCount={items.length}
        onSelectPage={() => setSelected(new Set(items.map((i) => i.account_id)))}
        onClear={() => setSelected(new Set())}
        onDelete={() => {}}
        className="mb-4"
      />

      <div className="space-y-4">
        {loading && !items.length && (
          <>
            <div className="rounded-2xl border border-slate-800/80 bg-slate-950/80 p-4">
              <Skeleton className="h-24 w-full" />
            </div>
            <div className="rounded-2xl border border-slate-800/80 bg-slate-950/80 p-4">
              <Skeleton className="h-24 w-full" />
            </div>
          </>
        )}
        {!loading && items.length === 0 && (
          <div className="rounded-2xl border border-slate-800/80 bg-slate-950/80 p-6 text-center text-sm text-foreground/70">
            Nenhuma assinatura encontrada para os filtros atuais.
          </div>
        )}
        {items.map((s) => (
          <SubscriptionCardFuturistic
            key={s.id}
            item={s}
            checked={selected.has(s.account_id)}
            onToggle={(ck) =>
              setSelected((prev) => {
                const n = new Set(prev)
                if (ck) n.add(s.account_id)
                else n.delete(s.account_id)
                return n
              })
            }
            expanded={expanded.has(s.account_id)}
            onToggleExpand={() => toggleExpand(s.account_id)}
            history={histories[s.account_id]}
          />
        ))}
      </div>

      <div className="mt-4 flex justify-end">
        <Pagination page={page} total={totalPages} onChange={setPage} />
      </div>
    </div>
  )
}
